import React, { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import AdminNavbar from "./AdminNavbar";

const Container = styled.div`
  min-height: 100vh;
  background: #f2f2f2;
  font-family: "Inter", sans-serif;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  padding: 20px;
`;

const Table = styled.table`
  width: 100%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border-collapse: collapse;
  overflow: hidden;

  th, td {
    padding: 12px 16px;
    border-bottom: 1px solid #eee;
    text-align: left;
  }

  th {
    background: #f9f9f9;
    font-weight: bold;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;

  input, select {
    padding: 8px;
    font-size: 14px;
    border-radius: 4px;
    border: 1px solid #ccc;
    flex: 1;
    min-width: 180px;
  }
`;

const TotalAmount = styled.div`
  background: #d1fae5;
  color: #047857;
  padding: 20px;
  margin-bottom: 20px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
`;

const AdminOrdersList = () => {
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({
    email: "",
    license: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });

  const navigate = useNavigate();

  // Place this above the component or at the top after imports
const licenseColorMap = {
  "STANDARD - 1 miesiąc": "#2196F3",
  "STANDARD - 6 miesięcy": "#1976D2",
  "STANDARD - 1 rok": "#0D47A1",
  "PRO - 1 miesiąc": "#4CAF50",
  "PRO - 6 miesięcy": "#388E3C",
  "PRO - 1 rok": "#1B5E20",
  "ENTERPRISE - 1 miesiąc": "#9C27B0",
  "ENTERPRISE - 6 miesięcy": "#7B1FA2",
  "ENTERPRISE - 1 rok": "#4A148C",
  "DEMO BASIC - 7 dni": "#FFC107",
  "DEMO EXTENDED - 15 dni": "#FF9800",
  "NO EXPIRE": "#FF5722",
};


  useEffect(() => {
    const adminUser = JSON.parse(localStorage.getItem("adminUser"));
    if (!adminUser || adminUser.userRole !== "SUPERADMIN") {
      toast.error("Brak dostępu.");
      navigate("/adminpanel/adminlogin");
      return;
    }

    axios.get("https://server229256.nazwa.pl/api/orders")
      .then((res) => setOrders(res.data))
      .catch(() => toast.error("Nie udało się pobrać zamówień."));
  }, [navigate]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const filteredOrders = orders.filter((order) => {
    const matchesEmail = order.email.toLowerCase().includes(filters.email.toLowerCase());
    const matchesLicense = order.license.toLowerCase().includes(filters.license.toLowerCase());
    const matchesStatus = order.status.toLowerCase().includes(filters.status.toLowerCase());

    const orderDate = new Date(order.creationTime);
    const dateFrom = filters.dateFrom ? new Date(filters.dateFrom) : null;
    const dateTo = filters.dateTo
      ? new Date(new Date(filters.dateTo).setHours(23, 59, 59, 999))
      : null;

    const matchesDateFrom = !dateFrom || orderDate >= dateFrom;
    const matchesDateTo = !dateTo || orderDate <= dateTo;

    return matchesEmail && matchesLicense && matchesStatus && matchesDateFrom && matchesDateTo;
  });

  const totalAmount = filteredOrders.reduce((sum, order) => {
    const price = parseFloat(order.price) || 0;
    return sum + price;
  }, 0);

  return (
    <Container>
      <AdminNavbar />
      <Content>
        <h2>Zamówienia</h2>

        <FilterBar>
          <input
            name="email"
            placeholder="Filtruj po e-mailu"
            value={filters.email}
            onChange={handleFilterChange}
          />
          <input
            name="license"
            placeholder="Filtruj po licencji"
            value={filters.license}
            onChange={handleFilterChange}
          />
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
          >
            <option value="">Wszystkie statusy</option>
            <option value="paid">Opłacone</option>
            <option value="not paid">Nieopłacone</option>
          </select>
          <input
            type="date"
            name="dateFrom"
            value={filters.dateFrom}
            onChange={handleFilterChange}
          />
          <input
            type="date"
            name="dateTo"
            value={filters.dateTo}
            onChange={handleFilterChange}
          />
        </FilterBar>

        <TotalAmount>
          Łączna suma zamówień: {totalAmount.toFixed(2)} zł
        </TotalAmount>

        <Table>
          <thead>
            <tr>
              <th>E-mail</th>
              <th>Kod twórcy</th>
              <th>Licencja</th>
              <th>Cena</th>
              <th>Data</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
  {filteredOrders.map((order) => (
    <tr key={order._id}>
      <td>{order.email}</td>
      <td>{order.creatorCode || "—"}</td>

      {/* LICENSE styled block */}
      <td>
        {order.license ? (
          <span
            style={{
              backgroundColor: licenseColorMap[order.license] || "#000",
              color: "white",
              padding: "5px 10px",
              borderRadius: "6px",
              fontWeight: "bold",
              display: "inline-block",
              minWidth: "140px",
              textAlign: "center",
            }}
          >
            {order.license}
          </span>
        ) : (
          "—"
        )}
      </td>

      <td>{order.price} zł</td>
      <td>{new Date(order.creationTime).toLocaleString("pl-PL")}</td>
      <td
        style={{
          color: order.status === "paid" ? "green" : "red",
          fontWeight: "bold",
        }}
      >
        {order.status === "paid" ? "Opłacone" : "Nieopłacone"}
      </td>
    </tr>
  ))}
</tbody>

        </Table>
      </Content>
    </Container>
  );
};

export default AdminOrdersList;
